import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, fromEvent, merge, timer } from 'rxjs';
import { LazyGlitchTipService } from './lazy-glitchtip.service';
import { pingEndpoint } from '../components/connection-indicator/connection-indicator.utils';
import { energyMonitoringService } from './energy-monitoring.service';

export type ConnectionStatus = 'offline' | 'slow' | 'fast';

@Injectable({
  providedIn: 'root',
})
export class ConnectionService {
  private connectionStatus = new BehaviorSubject<ConnectionStatus>('fast');
  private readonly SPEED_THRESHOLD_SLOW = 1.0; // 1.0 Mbps threshold for slow connection
  private readonly RTT_THRESHOLD_SLOW = 500; // 500ms RTT threshold for slow connection
  private readonly MAX_TEST_ATTEMPTS = 3; // Number of test attempts before deciding
  private readonly TEST_ENDPOINTS: string[] = [];
  private readonly DOWNLOAD_TEST_SIZE = 50 * 1024; // 50KB for download test
  private readonly DOWNLOAD_THRESHOLD_SLOW = 0.8; // 0.8 Mbps for download test
  private monitoringSubscription?: Subscription;

  constructor(private glitchTipService: LazyGlitchTipService) {
    // Get the base URL from the current document to handle subdomain deployments
    // This ensures assets are loaded from the correct path regardless of deployment location
    const baseUrl = new URL('./', document.baseURI).href;

    // Initialize endpoints with the correct base URL
    this.TEST_ENDPOINTS.push(
      `${baseUrl}assets/connection-test.json`,
      `${baseUrl}assets/icons/favicon.ico`,
      `${baseUrl}assets/manifest.webmanifest`
    );

    this.initializeConnectionMonitoring();
  }

  private initializeConnectionMonitoring() {
    // Monitor online/offline status
    this.monitoringSubscription = merge(
      timer(0), // Initial check with explicit timer
      fromEvent(window, 'online'),
      fromEvent(window, 'offline')
    ).subscribe(() => {
      if (!navigator.onLine) {
        this.connectionStatus.next('offline');
        return;
      }

      // If online, check connection speed
      this.checkConnectionSpeed();
    });
  }

  private async checkConnectionSpeed() {
    if (!navigator.onLine) {
      this.connectionStatus.next('offline');
      return;
    }

    try {
      // Check battery/save-data mode first
      const connection = (navigator as any).connection;
      if (connection && connection.saveData === true) {
        // User has requested data saving, treat as slow connection
        this.connectionStatus.next('slow');
        return;
      }

      // Try using Network Information API if available
      if (connection) {
        // Check if we have downlink information
        if (connection.downlink !== undefined) {
          // Network Information API with downlink available
          if (connection.downlink >= this.SPEED_THRESHOLD_SLOW) {
            this.connectionStatus.next('fast');
            return;
          }

          // Double-check with RTT if available before marking as slow
          if (connection.rtt !== undefined && connection.rtt < this.RTT_THRESHOLD_SLOW) {
            // If RTT is good, still consider it fast even with lower downlink
            this.connectionStatus.next('fast');
            return;
          }

          // Both downlink and RTT indicate slow connection
          if (connection.downlink < 0.5) {
            this.connectionStatus.next('slow');
            return;
          }

          // For borderline cases, continue to additional testing
        }

        // Check connection type if downlink not available
        if (connection.effectiveType) {
          const effectiveType = connection.effectiveType;
          if (effectiveType === '4g') {
            this.connectionStatus.next('fast');
            return;
          }
          if (effectiveType === 'slow-2g' || effectiveType === '2g') {
            this.connectionStatus.next('slow');
            return;
          }
          // For 3g, continue to additional testing
        }
      }

      // Fallback: Use RTT tests with multiple endpoints
      const results = await this.performMultipleRttTests();

      // Filter out outliers (drop 20% worst RTTs)
      const sortedResults = [...results].sort((a, b) => a - b);
      const trimmedResults = sortedResults.slice(0, Math.max(1, Math.ceil(sortedResults.length * 0.8)));
      const avgRtt = trimmedResults.reduce((sum, rtt) => sum + rtt, 0) / trimmedResults.length;

      // If RTT is very good, we can skip download test
      if (avgRtt < this.RTT_THRESHOLD_SLOW * 0.7) {
        this.connectionStatus.next('fast');
        return;
      }

      // If RTT is very bad, we can skip download test
      if (avgRtt > this.RTT_THRESHOLD_SLOW * 1.5) {
        this.connectionStatus.next('slow');
        return;
      }

      // For borderline RTT, perform a download test
      try {
        // Create a test blob of appropriate size
        const blob = new Blob([new ArrayBuffer(this.DOWNLOAD_TEST_SIZE)]);
        const blobUrl = URL.createObjectURL(blob);

        const downloadSpeed = await this.measureDownloadSpeed(blobUrl);
        URL.revokeObjectURL(blobUrl);

        this.connectionStatus.next(downloadSpeed >= this.DOWNLOAD_THRESHOLD_SLOW ? 'fast' : 'slow');
      } catch (downloadError) {
        // If download test fails, use RTT result
        console.warn('Download speed test failed:', downloadError);
        this.connectionStatus.next(avgRtt < this.RTT_THRESHOLD_SLOW ? 'fast' : 'slow');
      }
    } catch (error) {
      console.warn('Connection test failed:', error);
      this.glitchTipService.captureNetworkError(error as Error, {
        operation: 'speed_test',
        connectionType: this.connectionStatus.value,
        offline: !navigator.onLine,
        additionalData: {
          hasNetworkInfo: !!(navigator as any).connection,
          testEndpoints: this.TEST_ENDPOINTS.length,
        },
      });
      // Don't change status on error unless we're already offline
      if (!navigator.onLine) {
        this.connectionStatus.next('offline');
      }
    }
  }

  private async performMultipleRttTests(): Promise<number[]> {
    const rttResults: number[] = [];

    // Use Promise.allSettled to handle any failures
    const testPromises = this.TEST_ENDPOINTS.map((endpoint) => this.measureRtt(endpoint));
    const results = await Promise.allSettled(testPromises);

    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        rttResults.push(result.value);
      }
    });

    // If we got no results, use a default high value
    if (rttResults.length === 0) {
      return [this.RTT_THRESHOLD_SLOW + 100]; // Default to slow
    }

    return rttResults;
  }

  private async measureRtt(url: string): Promise<number> {
    energyMonitoringService.recordNetworkProbe();
    // Add cache-busting parameter to avoid caching
    const testUrl = `${url}?cachebust=${Date.now()}`;
    const startTime = performance.now();

    try {
      // Using HEAD request to minimize data transfer
      await fetch(testUrl, {
        method: 'HEAD',
        cache: 'no-store',
        credentials: 'same-origin', // Use same-origin for our own assets
        // Timeout after 2 seconds
        signal: AbortSignal.timeout(2000),
      });

      return performance.now() - startTime;
    } catch (error) {
      // Return a high RTT value on error
      return this.RTT_THRESHOLD_SLOW + 200;
    }
  }

  private async measureDownloadSpeed(url: string): Promise<number> {
    energyMonitoringService.recordNetworkProbe();
    const testUrl = `${url}?cachebust=${Date.now()}`;
    const startTime = performance.now();

    try {
      const response = await fetch(testUrl, {
        cache: 'no-store',
        signal: AbortSignal.timeout(5000), // Allow more time for download test
      });
      const blob = await response.blob();
      const durationSec = (performance.now() - startTime) / 1000;
      const sizeMB = blob.size / (1024 * 1024);

      // Return speed in Mbps
      return (sizeMB * 8) / durationSec;
    } catch (error) {
      throw error; // Let the calling function handle errors
    }
  }

  getConnectionStatus(): Observable<ConnectionStatus> {
    return this.connectionStatus.asObservable();
  }

  /** Manual trigger to refresh connection status */
  public refreshStatus(): void {
    if (!navigator.onLine) {
      this.connectionStatus.next('offline');
      return;
    }
    this.checkConnectionSpeed();
  }

  /** Ping a specific endpoint and return RTT, automatically updating connection status */
  public async pingEndpoint(url?: string): Promise<number> {
    const targetUrl = url || this.TEST_ENDPOINTS[0];
    if (!targetUrl) {
      throw new Error('No endpoint available for ping');
    }

    try {
      energyMonitoringService.recordNetworkProbe();
      const rtt = await pingEndpoint(targetUrl);

      // Update connection status based on ping results
      this.updateConnectionStatusFromPing(rtt);

      return rtt;
    } catch (error) {
      // If ping fails, check if we're offline
      if (!navigator.onLine) {
        this.connectionStatus.next('offline');
      }

      this.glitchTipService.captureNetworkError(error as Error, {
        operation: 'ping_endpoint',
        endpoint: targetUrl,
        additionalData: { offline: !navigator.onLine },
      });
      throw error;
    }
  }

  /** Update connection status based on ping RTT results */
  private updateConnectionStatusFromPing(rtt: number): void {
    if (!navigator.onLine) {
      this.connectionStatus.next('offline');
      return;
    }

    // Use the same RTT threshold as the existing logic
    if (rtt < this.RTT_THRESHOLD_SLOW * 0.7) {
      this.connectionStatus.next('fast');
    } else if (rtt > this.RTT_THRESHOLD_SLOW * 1.5) {
      this.connectionStatus.next('slow');
    } else {
      // For borderline cases, use the existing threshold
      this.connectionStatus.next(rtt < this.RTT_THRESHOLD_SLOW ? 'fast' : 'slow');
    }
  }

  /** Public method to update connection status from external ping results */
  public updateConnectionFromPing(rtt: number): void {
    this.updateConnectionStatusFromPing(rtt);
  }

  /** Set connection status to offline (for external checks that determine no connectivity) */
  public setOfflineStatus(): void {
    this.connectionStatus.next('offline');
  }

  /** Clean up resources and stop monitoring */
  public destroy(): void {
    this.monitoringSubscription?.unsubscribe();
    this.connectionStatus.complete();
  }
}
